import React, { useState } from 'react';
import { ArrowRight } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const developmentServices = [
  {
    title: 'Software product development',
    description: 'We create security business software to help companies succeed in their business processes.',
    href: '/development/software-product-development',
  },
  {
    title: 'Web application development',
    description: 'By specializing in creating apps, we stand out in providing personalized and interactive user experiences and representing a unique security value in the market.',
    href: '/development/web-application-development',
  },
  {
    title: 'DevNetOps services',
    description: 'Enable complete management & automated deployment of the apps through custom CI/CD pipeline development & configuration management tools.',
    href: '/development/dev-net-ops-services',
  },
  {
    title: 'IT consultant services',
    description: "We improve your firm's productivity, expandability and profitability by implementing the right technology.",
    href: '/development/it-consulting-services',
  },
];

const otherServices = [
  {
    title: 'Cybersecurity Assessment',
    description: 'Comprehensive security audits, penetration testing, and vulnerability assessments to protect your business.',
    href: '/services/cybersecurity',
  },
  {
    title: 'Cloud Infrastructure',
    description: 'Design, deployment, and management of scalable cloud solutions on AWS, Azure, and Google Cloud.',
    href: '/services/cloud-infrastructure',
  },
  {
    title: 'UI/UX Design',
    description: 'User-centered design that creates seamless, intuitive, and visually stunning digital experiences.',
    href: '/services/ui-ux-design',
  },
  {
    title: 'Data Analytics',
    description: 'Transform raw data into actionable insights with advanced analytics, BI dashboards, and ML models.',
    href: '/services/data-analytics',
  },
];

export default function WhatWeOffer() {
  const [activeTab, setActiveTab] = useState('development');
  const navigate = useNavigate();

  const currentServices = activeTab === 'development' ? developmentServices : otherServices;

  return (
    <>
      <style>{`
        @media (max-width: 768px) {
          .what-we-offer-grid {
            grid-template-columns: 1fr !important;
            gap: 40px !important;
          }
          .what-we-offer-section {
            padding: 60px 24px !important;
          }
          .tab-buttons {
            flex-direction: column !important;
          }
          .tab-buttons button {
            width: 100% !important;
          }
          .offer-image {
            display: none !important;
          }
        }
      `}</style>
      
      <section className="what-we-offer-section" style={{ padding: '100px 24px', background: 'white' }}>
        <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
          <div className="what-we-offer-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '80px', alignItems: 'start' }}>
            {/* Left column */}
            <div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '16px' }}>
                <div style={{ width: '24px', height: '3px', backgroundColor: '#0099CC', borderRadius: '2px' }}></div>
                <span style={{ fontSize: '14px', fontWeight: '600', color: '#0099CC', textTransform: 'uppercase', letterSpacing: '1px' }}>
                  Our Services
                </span>
              </div>
              <h2 style={{ fontSize: 'clamp(28px, 3vw, 40px)', fontWeight: '700', color: '#111827', marginBottom: '16px', letterSpacing: '-0.5px' }}>
                What we offer
              </h2>
              <p style={{ fontSize: '17px', color: '#6B7280', lineHeight: 1.7, marginBottom: '40px', maxWidth: '460px' }}>
                Custom software development services unlock potential for companies in every industry.
              </p>

              {/* Tab buttons */}
              <div className="tab-buttons" style={{ display: 'flex', gap: '12px', marginBottom: '48px' }}>
                <button
                  onClick={() => setActiveTab('development')}
                  style={{
                    padding: '10px 24px',
                    borderRadius: '8px',
                    border: 'none',
                    cursor: 'pointer',
                    fontSize: '15px',
                    fontWeight: '600',
                    transition: 'background-color 0.2s ease, color 0.2s ease',
                    backgroundColor: activeTab === 'development' ? '#111827' : '#F3F4F6',
                    color: activeTab === 'development' ? 'white' : '#374151',
                  }}
                >
                  Development
                </button>
                <button
                  onClick={() => setActiveTab('services')}
                  style={{
                    padding: '10px 24px',
                    borderRadius: '8px',
                    border: 'none',
                    cursor: 'pointer',
                    fontSize: '15px',
                    fontWeight: '600',
                    transition: 'background-color 0.2s ease, color 0.2s ease',
                    backgroundColor: activeTab === 'services' ? '#111827' : '#F3F4F6',
                    color: activeTab === 'services' ? 'white' : '#374151',
                  }}
                >
                  Services
                </button>
              </div>

              {/* Service list */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: '32px' }}>
                {currentServices.map((service, idx) => (
                  <div key={idx} style={{ borderBottom: idx < currentServices.length - 1 ? '1px solid #F3F4F6' : 'none', paddingBottom: idx < currentServices.length - 1 ? '32px' : '0' }}>
                    <h3 style={{ fontSize: '18px', fontWeight: '700', color: '#111827', marginBottom: '10px' }}>
                      {service.title}
                    </h3>
                    <p style={{ fontSize: '15px', color: '#6B7280', lineHeight: 1.7, marginBottom: '12px' }}>
                      {service.description}
                    </p>
                    <button
                      onClick={() => navigate(service.href)}
                      style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '6px',
                        color: '#0099CC',
                        background: 'none',
                        border: 'none',
                        cursor: 'pointer',
                        fontSize: '14px',
                        fontWeight: '600',
                        padding: 0,
                        transition: 'gap 0.2s ease',
                      }}
                      onMouseEnter={e => e.currentTarget.style.gap = '10px'}
                      onMouseLeave={e => e.currentTarget.style.gap = '6px'}
                    >
                      Learn more <ArrowRight size={16} />
                    </button>
                  </div>
                ))}
              </div>
            </div>

            {/* Right column - image */}
            <div className="offer-image" style={{ position: 'sticky', top: '100px' }}>
              <div style={{ borderRadius: '20px', overflow: 'hidden', lineHeight: 0 }}>
                <img 
                  src="/pictures/how_we_do_it_f7a18d27ba.jpg" 
                  alt="What we offer" 
                  style={{ width: '100%', height: 'auto', display: 'block' }}
                />
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
